import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CALENDAR_AD_HOC_API_BASE_URL } from 'src/calendar/constants';
import { focusCalendar } from 'src/calendar/ducks/calendar';
import { unsuccessfulResponseCapturer } from 'src/calendar/hooks';
import type { AdhocCalendarEvent, AdHocEventFormState } from 'src/calendar/types';
import { prepareAdHocEventDataForAPI } from 'src/calendar/utils/unavailableEditorUtils';
import { PATHS, TIME_12 } from 'src/common/constants';
import useDispatch from 'src/common/hooks/useAppDispatch';
import { trackEvent } from 'src/common/tracking';
import { formatDate } from 'src/common/utils/dateHelpers';
import apiFetch from 'src/core/fetch';
import type { FullBoatDetail } from 'src/types/boat/BoatDetail';
import NotificationMessage from '../NotificationMessage';
import UpdateAvailabilityEditor from './UpdateAvailabilityEditor';
import UpdateAvailabilityPrompt from './UpdateAvailabilityPrompt';
import GA_EVENTS from './constants';
import s from './UpdateAvailability.module.scss';

// Utility function to format the availability message
const getAvailabilityMessage = ({
  all_day,
  sameDay,
  startDateFormatted,
  endDateFormatted,
  startTimeFormatted,
  endTimeFormatted,
}: {
  all_day?: boolean;
  sameDay: boolean;
  startDateFormatted: string;
  endDateFormatted: string;
  startTimeFormatted: string;
  endTimeFormatted: string;
}) => {
  if (all_day) {
    if (sameDay) {
      return `${startDateFormatted}`;
    }
    return `${startDateFormatted} – ${endDateFormatted}`;
  }
  if (sameDay) {
    return `${startTimeFormatted} – ${endTimeFormatted} on ${startDateFormatted}`;
  }
  return `${startDateFormatted} from ${startTimeFormatted} till ${endTimeFormatted} on ${endDateFormatted}`;
};

type CalendarAvailabilityManagerProps = {
  boatId: FullBoatDetail['boat_id'];
  boat: FullBoatDetail;
  endDateString: string;
  showPrompt: boolean;
  startDateString: string;
};

const CalendarAvailabilityManager: FC<CalendarAvailabilityManagerProps> = ({
  boatId,
  boat,
  endDateString,
  showPrompt,
  startDateString,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showUpdateModal, setShowUpdateModal] = useState(showPrompt);
  const [showEditModal, setShowEditModal] = useState(false);
  const initialStartDate = useMemo(() => new Date(startDateString), [startDateString]);
  const initialEndDate = useMemo(() => new Date(endDateString), [endDateString]);

  const onCalendarClick = useCallback((eventDate, eventId) => {
    history.push(PATHS.CALENDAR);
    dispatch(focusCalendar({
      selectedDate: eventDate,
      focusedAdHocEventId: eventId,
      scrollToEvent: true,
    }));
  }, [dispatch, history]);

  const handleAdHocEvent = useCallback(async (
    eventData: AdHocEventFormState,
    onSuccess?: () => void,
  ) => {
    const preparedData = prepareAdHocEventDataForAPI({
      ...eventData,
      boat_ids: [boatId],
    });

    return apiFetch(CALENDAR_AD_HOC_API_BASE_URL, {
      method: 'POST',
      body: JSON.stringify(preparedData),
    })
      .then((response) => response.json())
      .then(({
        all_day,
        event_date,
        event_end_date,
        event_start_time,
        event_end_time,
        id,
      }: AdhocCalendarEvent) => {
        if (onSuccess) onSuccess();

        const startDate = new Date(`${event_date}T${event_start_time || '00:00'}`);
        const endDate = new Date(`${event_end_date}T${event_end_time || '00:00'}`);

        // Show success notification with event ID for direct navigation
        toast(
          <NotificationMessage
            classNameModifier="info fixedWidth"
            outdent={false}
            withIcon={false}
          >
            <h4 className={s.notificationTitle}>
              Availability Updated
            </h4>
            We&apos;ve blocked{' '}
            {getAvailabilityMessage({
              all_day,
              sameDay: event_date === event_end_date,
              startDateFormatted: formatDate(startDate, 'MMMM d, yyyy', {}),
              endDateFormatted: formatDate(endDate, 'MMMM d, yyyy', {}),
              startTimeFormatted: formatDate(startDate, TIME_12, {}),
              endTimeFormatted: formatDate(endDate, TIME_12, {}),
            })}
            {' '}for {boat.shortname}.{' '}
            <button
              className={s.calendarButton}
              onClick={() => onCalendarClick(event_date, id)}
              type="button"
            >
              Check your calendar
            </button>{' '}
            to adjust.
          </NotificationMessage>,
          { autoClose: 5000 },
        );
      })
      .catch(unsuccessfulResponseCapturer);
  }, [boat.shortname, boatId, onCalendarClick]);

  // Handle direct block without editing
  const handleBlockTime = useCallback(() => handleAdHocEvent(
    {
      all_boats: false,
      // By default (without explicitly setting in editor) block exact trip range
      all_day: false,
      boat_ids: [boatId],
      event_date: formatDate(initialStartDate, 'yyyy-MM-dd', {}),
      event_end_date: formatDate(initialEndDate, 'yyyy-MM-dd', {}),
      event_end_time: formatDate(initialEndDate, 'HH:mm', {}),
      event_start_time: formatDate(initialStartDate, 'HH:mm', {}),
      frequency: null,
      repeat_count: 0,
      repetitions_end_date: null,
    },
    () => trackEvent(GA_EVENTS.CALENDAR_EVENT_CREATED),
  ), [
    handleAdHocEvent,
    boatId,
    initialStartDate,
    initialEndDate,
  ]);

  // Handle save the editor form
  const onSave = useCallback((editorData: AdHocEventFormState) => {
    // Track click on Save button
    trackEvent(GA_EVENTS.UNAVAILABLE_EDITOR_MODAL.SAVE);

    return handleAdHocEvent(
      editorData,
      () => {
        setShowEditModal(false);
        // Track calendar event created after successful saving
        trackEvent(GA_EVENTS.CALENDAR_EVENT_CREATED);
      },
    );
  }, [handleAdHocEvent]);

  // If the inquiry has been declined, show the update availability modal
  useEffect(() => {
    if (showPrompt) {
      setShowUpdateModal(true);
    }
  }, [showPrompt]);

  return (
    <>
      <UpdateAvailabilityPrompt
        closeModal={() => setShowUpdateModal(false)}
        isOpen={showUpdateModal}
        boatName={boat.shortname}
        formattedStartDate={formatDate(initialStartDate, 'MMMM d, yyyy', {})}
        formattedEndDate={formatDate(initialEndDate, 'MMMM d, yyyy', {})}
        startTime={`${formatDate(initialStartDate, TIME_12, {})}`}
        endTime={`${formatDate(initialEndDate, TIME_12, {})}`}
        onYes={handleBlockTime}
        onEdit={() => {
          setShowEditModal(true);
          setShowUpdateModal(false);
        }}
      />

      <UpdateAvailabilityEditor
        closeModal={() => {
          setShowEditModal(false);
          trackEvent(GA_EVENTS.UNAVAILABLE_EDITOR_MODAL.CLOSE);
        }}
        isOpen={showEditModal}
        boatId={boatId}
        boat={boat}
        formattedStartDate={formatDate(initialStartDate, 'yyyy-MM-dd', {})}
        formattedStartTime={formatDate(initialStartDate, 'HH:mm', {})}
        formattedEndDate={formatDate(initialEndDate, 'yyyy-MM-dd', {})}
        formattedEndTime={formatDate(initialEndDate, 'HH:mm', {})}
        onSave={onSave}
      />
    </>
  );
};

export default CalendarAvailabilityManager;
